import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { COL } from '@eprintsmart/shared';
import { collection, deleteDoc, doc, getDocs, orderBy, query } from 'firebase/firestore';
const when = (v) => {
    const ms = typeof v === 'number' ? v : v?.seconds ? v.seconds * 1000 : null;
    return ms ? new Date(ms).toLocaleString('en-IN') : '—';
};
export function SheetLibrary({ db, render, canDelete = false, }) {
    const [jobs, setJobs] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        try {
            const snap = await getDocs(query(collection(db, COL.impositionJobs), orderBy('createdAt', 'desc')));
            setJobs(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load saved sheets.');
            setJobs([]);
        }
    }, [db]);
    useEffect(() => { void load(); }, [load]);
    const onRender = async (id) => {
        setBusy(id);
        setError(null);
        try {
            const { url } = await render({ jobId: id });
            // A new tab, not a download: the renderer returns a short-lived signed
            // URL and the operator usually wants to eyeball the sheet before printing.
            window.open(url, '_blank', 'noopener');
            await load();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Could not render that sheet.');
        }
        finally {
            setBusy(null);
        }
    };
    const onDelete = async (id) => {
        if (!confirm('Delete this saved sheet? The layout is lost; any PDF already downloaded is unaffected.'))
            return;
        setBusy(id);
        try {
            await deleteDoc(doc(db, COL.impositionJobs, id));
            await load();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : 'Could not delete that sheet.');
        }
        finally {
            setBusy(null);
        }
    };
    if (jobs === null)
        return _jsx("p", { className: "tiny muted", children: "Loading saved sheets\u2026" });
    return (_jsxs("div", { className: "printlab-root", children: [error && _jsx("p", { className: "tiny", style: { color: 'crimson' }, children: error }), jobs.length === 0 ? (_jsx("p", { className: "tiny muted", children: "No saved sheets yet. Lay one out in the print lab and save it \u2014 it appears here for anyone in the shop." })) : (_jsx("div", { className: "sheet-grid", children: jobs.map((j) => (_jsxs("div", { className: "sheet-card", children: [j.source?.previewUrl ? (
                        // Signed read URLs expire, so a dead thumbnail is expected on
                        // older sheets and must not break the row.
                        _jsx("img", { src: j.source.previewUrl, alt: "", className: "sheet-card__thumb" })) : (_jsx("div", { className: "sheet-card__thumb sheet-card__thumb--empty" })), _jsxs("div", { className: "sheet-card__body", children: [_jsx("strong", { children: j.title ?? 'Untitled sheet' }), _jsxs("span", { className: "tiny muted", children: [j.perSheet ?? '?', " up", j.sheetsNeeded ? ` · ${j.sheetsNeeded} sheet${j.sheetsNeeded === 1 ? '' : 's'}` : '', ' · ', when(j.createdAt)] }), j.output?.renderedAt && (_jsxs("span", { className: "tiny muted", children: ["Last rendered ", new Date(j.output.renderedAt).toLocaleString('en-IN')] })), _jsxs("div", { className: "chip-row", style: { marginTop: '0.4rem' }, children: [_jsx("button", { type: "button", className: "btn btn--primary", disabled: busy === j.id, onClick: () => void onRender(j.id), children: busy === j.id ? 'Rendering…' : 'Render PDF' }), canDelete && (_jsx("button", { type: "button", className: "btn", disabled: busy === j.id, onClick: () => void onDelete(j.id), children: "Delete" }))] })] })] }, j.id))) }))] }));
}
